import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress,
  Divider, Grid, IconButton, Stack, TableCell, TableRow, TextField,
  Typography, useMediaQuery, useTheme
} from '@mui/material';
import { Add, AutoAwesome, Delete as DeleteIcon, Edit as EditIcon, Refresh } from '@mui/icons-material';
import { reportCardAPI, academicAPI, studentAPI } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';
import { DataTable, TD_SX, useAppSnackbar, useConfirmDialog } from '../common/TableDesign';
import GeneralCommentFormDialog from './forms/GeneralCommentFormDialog';

const extractCollection = (r) => {
  const p = r?.data !== undefined ? r.data : r;
  if (Array.isArray(p)) return p;
  if (Array.isArray(p?.data)) return p.data;
  if (Array.isArray(p?.data?.data)) return p.data.data;
  return [];
};
const err       = (e, fb = 'Operation failed') => e?.response?.data?.message || e?.response?.data?.error || fb;
const termLabel = (t) => t ? `${t.name} ${t.year || ''}`.trim() : '';
const studName  = (s) => s?.full_name || `${s?.last_name || ''} ${s?.first_name || ''}`.trim() || s?.registration_no || '';
const acYear    = (t) => Number(t?.academic_year || t?.year || new Date().getFullYear());

const INITIAL_FORM = { student: null, term: null, class_teacher_comment: '', headteacher_comment: '' };
const COLUMNS      = ['Student', 'Class', 'Stream', 'Term', 'Class Teacher Comment', 'Headteacher Comment', 'Actions'];
const PAGE_SIZE    = 15;

export default function GeneralComments() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const { can } = useAccessControl();
  const canRead   = can(RESOURCES.REPORT_CARD.GENERAL_COMMENTS, ACTIONS.READ);
  const canCreate = can(RESOURCES.REPORT_CARD.GENERAL_COMMENTS, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.REPORT_CARD.GENERAL_COMMENTS, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.REPORT_CARD.GENERAL_COMMENTS, ACTIONS.DELETE);
  const { hasPermission, RESOURCES: R, ACTIONS: A } = usePermissions();
  const canCT = hasPermission(R.REPORT_CARD.GENERAL_COMMENTS, A.WRITE_CLASS_TEACHER_COMMENT);
  const canHT = hasPermission(R.REPORT_CARD.GENERAL_COMMENTS, A.WRITE_HEADTEACHER_COMMENT);

  // data
  const [comments,     setComments]    = useState([]);
  const [terms,        setTerms]       = useState([]);
  const [students,     setStudents]    = useState([]);
  const [classStreams, setClassStreams] = useState([]);

  // loading / ui
  const [loadingSetup,    setLoadingSetup]    = useState(true);
  const [loadingComments, setLoadingComments] = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [bulkGenerating,  setBulkGenerating]  = useState(false);
  // dialog: null | 'ct' | 'ht' | 'edit'
  const [dialogMode,    setDialogMode]    = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form,          setForm]          = useState(INITIAL_FORM);

  // filters
  const [termFilter,    setTermFilter]    = useState(null);
  const [classFilter,   setClassFilter]   = useState(null);
  const [streamFilter,  setStreamFilter]  = useState(null);
  const [studentFilter, setStudentFilter] = useState(null);
  const [search,        setSearch]        = useState('');
  const [page,          setPage]          = useState(1);
  const [pageSize,      setPageSize]      = useState(PAGE_SIZE);

  // ── fetch ──────────────────────────────────────────────────────────────────
  const fetchSetup = useCallback(async () => {
    setLoadingSetup(true);
    try {
      const [termRes, studentRes, csRes] = await Promise.all([
        academicAPI.getTerms(),
        studentAPI.getActive({ per_page: 2000 }),
        academicAPI.getClassStreamsForFilter(),
      ]);
      const fetchedTerms = extractCollection(termRes);
      setTerms(fetchedTerms);
      setStudents(extractCollection(studentRes));
      setClassStreams(extractCollection(csRes));
      const current = fetchedTerms.find((t) => t.is_current) || null;
      if (current) setTermFilter(current);
    } catch (e) {
      showSnackbar(err(e, 'Failed to load setup data'), 'error');
    } finally {
      setLoadingSetup(false);
    }
  }, []); // eslint-disable-line

  const loadComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      const params = { per_page: 1000 };
      if (termFilter?.id)    { params.term_id = termFilter.id; params.academic_year = acYear(termFilter); }
      if (studentFilter?.id)   params.student_id = studentFilter.id;
      setComments(extractCollection(await reportCardAPI.getGeneralComments(params)));
    } catch (e) {
      setComments([]);
      showSnackbar(err(e, 'Failed to load comments'), 'error');
    } finally {
      setLoadingComments(false);
    }
  }, [studentFilter, termFilter]); // eslint-disable-line

  useEffect(() => { fetchSetup(); },   [fetchSetup]);
  useEffect(() => { loadComments(); }, [loadComments]);

  // ── derived lists ──────────────────────────────────────────────────────────
  const classes = useMemo(() => {
    const seen = new Map();
    classStreams.forEach((cs) => {
      if (cs.class_id && !seen.has(cs.class_id)) {
        seen.set(cs.class_id, { id: cs.class_id, name: cs.name?.split('-')[0]?.trim() || `Class ${cs.class_id}` });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [classStreams]);

  const streams = useMemo(() => {
    const pool = classFilter ? classStreams.filter((cs) => cs.class_id === classFilter.id) : classStreams;
    const seen = new Map();
    pool.forEach((cs) => {
      if (cs.stream_id && !seen.has(cs.stream_id))
        seen.set(cs.stream_id, { id: cs.stream_id, name: cs.stream_name || `Stream ${cs.stream_id}` });
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [classStreams, classFilter]);

  const matchingCsIds = useMemo(() => {
    if (!classFilter && !streamFilter) return null;
    return new Set(
      classStreams
        .filter((cs) => (!classFilter || cs.class_id === classFilter.id) && (!streamFilter || cs.stream_id === streamFilter.id))
        .map((cs) => cs.id)
    );
  }, [classStreams, classFilter, streamFilter]);

  const filteredStudents = useMemo(() => {
    if (!matchingCsIds) return students;
    return students.filter((s) => matchingCsIds.has(s.class_stream_id));
  }, [students, matchingCsIds]);

  const getStudentClass  = (s) => { const cs = classStreams.find((c) => c.id === s?.class_stream_id); return cs?.name?.split('-')[0]?.trim() || ''; };
  const getStudentStream = (s) => { const cs = classStreams.find((c) => c.id === s?.class_stream_id); return cs?.stream_name || ''; };

  const filtered = useMemo(() => {
    let list = comments;
    if (matchingCsIds) list = list.filter((c) => matchingCsIds.has(c.student?.class_stream_id));
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      studName(c?.student).toLowerCase().includes(q) ||
      termLabel(c?.term).toLowerCase().includes(q)   ||
      String(c?.class_teacher_comment || '').toLowerCase().includes(q) ||
      String(c?.headteacher_comment   || '').toLowerCase().includes(q)
    );
  }, [comments, matchingCsIds, search]);

  const total       = filtered.length;
  const lastPage    = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, lastPage);
  const paged       = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pagination  = { current_page: currentPage, last_page: lastPage, per_page: pageSize, total };

  // ── actions ────────────────────────────────────────────────────────────────
  const handleBulkGenerate = async () => {
    if (!termFilter?.id) { showSnackbar('Select a term first', 'error'); return; }
    setBulkGenerating(true);
    try {
      const res = await reportCardAPI.generateComments({
        term_id: termFilter.id, academic_year: acYear(termFilter),
        subject: false, class_teacher: canCT, headteacher: canHT, preserve_manual: true,
      });
      const { class_teacher_generated, headteacher_generated, skipped } = res.data?.data || {};
      const parts = [];
      if (canCT) parts.push(`Class Teacher: ${class_teacher_generated ?? 0}`);
      if (canHT) parts.push(`Headteacher: ${headteacher_generated ?? 0}`);
      showSnackbar(`Generated — ${parts.join(', ')}, Skipped: ${skipped ?? 0}`);
      loadComments();
    } catch (e) {
      showSnackbar(err(e, 'Bulk generate failed'), 'error');
    } finally {
      setBulkGenerating(false);
    }
  };

  const resetForm = () => { setForm(INITIAL_FORM); setEditingRecord(null); setDialogMode(null); };

  const handleAdd = (mode) => {
    setForm({ ...INITIAL_FORM, term: termFilter || null });
    setDialogMode(mode);
  };

  const handleEdit = (c) => {
    setEditingRecord(c);
    setForm({
      student: c.student || null, term: c.term || null,
      class_teacher_comment: c.class_teacher_comment || '',
      headteacher_comment:   c.headteacher_comment   || '',
    });
    setDialogMode('edit');
  };

  const handleDelete = (c) => {
    confirm({
      title: 'Delete General Comment',
      message: `Delete comment for "${studName(c.student)}"? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await reportCardAPI.deleteGeneralComment(c.id);
          showSnackbar('Comment deleted');
          loadComments();
        } catch (e) { showSnackbar(err(e, 'Failed to delete'), 'error'); }
      },
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const ct = form.class_teacher_comment.trim();
    const ht = form.headteacher_comment.trim();
    if (!form.student?.id || !form.term?.id || (!ct && !ht)) {
      showSnackbar('Student, term, and at least one comment are required', 'error'); return;
    }
    setSaving(true);
    try {
      const payload = {
        student_id: form.student.id, academic_year: acYear(form.term),
        term_id: form.term.id,
        class_teacher_comment: ct || null,
        headteacher_comment:   ht || null,
      };
      if (editingRecord) {
        await reportCardAPI.updateGeneralComment(editingRecord.id, payload);
        showSnackbar('Comment updated');
      } else {
        await reportCardAPI.createGeneralComment(payload);
        showSnackbar('Comment saved');
      }
      resetForm();
      loadComments();
    } catch (e) {
      showSnackbar(err(e, 'Failed to save comment'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!canRead) return <Alert severity="error">You do not have permission to view general comments.</Alert>;

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <Box>
      <ModulePageHeader
        title="General Comments"
        subtitle="Manage class teacher and headteacher comments per student"
        actions={[
          <Button key="bulk" variant="outlined"
            startIcon={bulkGenerating ? <CircularProgress size={16} /> : <AutoAwesome />}
            onClick={handleBulkGenerate}
            disabled={bulkGenerating || (!canCT && !canHT)} fullWidth={isMobile}>
            Auto Generate
          </Button>,
          <Button key="refresh" variant="outlined" startIcon={<Refresh />}
            onClick={() => { fetchSetup(); loadComments(); }}
            disabled={loadingSetup || loadingComments} fullWidth={isMobile}>
            Refresh
          </Button>,
          canCT && (
            <Button key="add-ct" variant="outlined" startIcon={<Add />}
              onClick={() => handleAdd('ct')} fullWidth={isMobile}>
              Add Class Teacher
            </Button>
          ),
          canHT && (
            <Button key="add-ht" variant="contained" startIcon={<Add />}
              onClick={() => handleAdd('ht')} fullWidth={isMobile}>
              Add Headteacher
            </Button>
          ),
        ].filter(Boolean)}
      />

      {/* ── Filters ── */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Filters</Typography>
          <Grid container spacing={2}>
            {/* Row 1 */}
            <Grid item xs={12} sm={4}>
              <Autocomplete options={terms} value={termFilter}
                onChange={(_, v) => { setTermFilter(v); setPage(1); }}
                getOptionLabel={termLabel} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, t) => (
                  <Box component="li" {...props} key={t.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{termLabel(t)}</Typography>
                      {t.is_current && <Chip label="Current" size="small" color="success" />}
                    </Stack>
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="Term" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete options={classes} value={classFilter}
                onChange={(_, v) => { setClassFilter(v); setStreamFilter(null); setStudentFilter(null); setPage(1); }}
                getOptionLabel={(c) => c.name} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Class" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete options={streams} value={streamFilter}
                onChange={(_, v) => { setStreamFilter(v); setStudentFilter(null); setPage(1); }}
                getOptionLabel={(s) => s.name} isOptionEqualToValue={(o, v) => o.id === v.id}
                disabled={streams.length === 0}
                renderInput={(p) => <TextField {...p} label="Stream" size="small" />}
              />
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* Row 2 */}
            <Grid item xs={12} sm={4}>
              <Autocomplete options={filteredStudents} value={studentFilter}
                onChange={(_, v) => { setStudentFilter(v); setPage(1); }}
                getOptionLabel={studName} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, s) => (
                  <Box component="li" {...props} key={s.id}>
                    <Box>
                      <Typography variant="body2">{studName(s)}</Typography>
                      {s.registration_no && <Typography variant="caption" color="text.secondary">{s.registration_no}</Typography>}
                    </Box>
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="Student" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth size="small" label="Search"
                placeholder="Student name, comment text..."
                value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Active filter chips ── */}
      {!loadingComments && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip label={`${total} result${total !== 1 ? 's' : ''}`} size="small" />
          {termFilter    && <Chip label={termLabel(termFilter)}   size="small" color="primary"   variant="outlined" onDelete={() => setTermFilter(null)} />}
          {classFilter   && <Chip label={classFilter.name}        size="small" color="secondary" variant="outlined" onDelete={() => { setClassFilter(null); setStreamFilter(null); setStudentFilter(null); }} />}
          {streamFilter  && <Chip label={streamFilter.name}       size="small" color="secondary" variant="outlined" onDelete={() => { setStreamFilter(null); setStudentFilter(null); }} />}
          {studentFilter && <Chip label={studName(studentFilter)} size="small" variant="outlined" onDelete={() => setStudentFilter(null)} />}
        </Stack>
      )}

      {/* ── Results ── */}
      <Card>
        <CardContent>
          {loadingComments ? (
            <Box py={6} textAlign="center"><CircularProgress size={28} /></Box>
          ) : filtered.length === 0 ? (
            <EmptyRecordsState message="No general comments found." />
          ) : isMobile ? (
            <>
              <ResponsiveRecordList
                items={paged}
                renderItem={(c) => (
                  <Stack spacing={0.75}>
                    <Typography fontWeight={700} variant="body2">{studName(c?.student)}</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {getStudentClass(c?.student)  && <Chip label={getStudentClass(c?.student)}  size="small" variant="outlined" />}
                      {getStudentStream(c?.student) && <Chip label={getStudentStream(c?.student)} size="small" variant="outlined" />}
                      <Chip label={termLabel(c?.term)} size="small" color="primary" variant="outlined" />
                    </Stack>
                    {c?.class_teacher_comment && (
                      <Box>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">Class Teacher</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{c.class_teacher_comment}</Typography>
                      </Box>
                    )}
                    {c?.headteacher_comment && (
                      <Box>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">Headteacher</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{c.headteacher_comment}</Typography>
                      </Box>
                    )}
                    <Box textAlign="right">
                      {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(c)}><EditIcon fontSize="small" /></IconButton>}
                      {canDelete && <IconButton size="small" color="error"   onClick={() => handleDelete(c)}><DeleteIcon fontSize="small" /></IconButton>}
                    </Box>
                  </Stack>
                )}
              />
              <TablePagination pagination={pagination} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} isMobile />
            </>
          ) : (
            <>
              <DataTable columns={COLUMNS} data={paged} loading={false}
                emptyMessage="No general comments found." minWidth={1000}
                renderRow={(c) => (
                  <TableRow key={c.id} hover>
                    <TableCell sx={TD_SX}>
                      <Typography variant="body2" fontWeight={600}>{studName(c?.student)}</Typography>
                      {c?.student?.registration_no && <Typography variant="caption" color="text.secondary">{c.student.registration_no}</Typography>}
                    </TableCell>
                    <TableCell sx={TD_SX}><Typography variant="body2">{getStudentClass(c?.student)}</Typography></TableCell>
                    <TableCell sx={TD_SX}><Typography variant="body2">{getStudentStream(c?.student)}</Typography></TableCell>
                    <TableCell sx={TD_SX}><Chip label={termLabel(c?.term)} size="small" variant="outlined" /></TableCell>
                    <TableCell sx={{ ...TD_SX, maxWidth: 260 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{c?.class_teacher_comment || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...TD_SX, maxWidth: 260 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{c?.headteacher_comment || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={TD_SX} align="right">
                      {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(c)}><EditIcon fontSize="small" /></IconButton>}
                      {canDelete && <IconButton size="small" color="error"   onClick={() => handleDelete(c)}><DeleteIcon fontSize="small" /></IconButton>}
                    </TableCell>
                  </TableRow>
                )}
              />
              <TablePagination pagination={pagination} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} isMobile={isMobile} />
            </>
          )}
        </CardContent>
      </Card>

      <GeneralCommentFormDialog
        open={!!dialogMode} onClose={resetForm} onSubmit={handleSubmit}
        editingRecord={editingRecord} formData={form} setFormData={setForm}
        saving={saving} isMobile={isMobile}
        students={students} terms={terms} classStreams={classStreams}
        mode={dialogMode}
        canCT={canCT} canHT={canHT}
      />
      {ConfirmDialogComponent}
      {SnackbarComponent}
    </Box>
  );
}
